import { useLocalSearchParams } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    ScreenContainer,
    SegmentedControl,
    Spinner,
    View,
    YStack,
} from 'smoothie';

import { isNonMobileWeb } from '../../../../../../packages/smoothie/src/constants';
import { useTrackViewOnFocus } from '../../../events/hooks/useTrackViewOnFocus';
import { useGetApexAccountDocuments } from '../../hooks/useGetApexAccountDocuments';
import { DocumentLinkList } from './DocumentLinkList';

enum DocumentType {
    STATEMENT = 'statement',
    TRADE_CONFIRMATION = 'trade_confirmation',
}

const DocumentTypeName = {
    [DocumentType.STATEMENT]: 'statements',
    [DocumentType.TRADE_CONFIRMATION]: 'trade confirmations',
};

export const InvestmentStatements = () => {
    const [documentType, setDocumentType] = useState(DocumentType.STATEMENT);

    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const {
        isLoading: isLoadingDocuments,
        data: accountDocuments,
        getDocumentDownloadUrl,
    } = useGetApexAccountDocuments(apexAccountId);

    const statementDocuments = useMemo(
        () => accountDocuments?.statements ?? [],
        [accountDocuments],
    );

    const confirmationDocuments = useMemo(
        () => accountDocuments?.tradeConfirmations ?? [],
        [accountDocuments],
    );

    const selectedDocuments = useMemo(() => {
        return documentType === DocumentType.STATEMENT
            ? statementDocuments
            : confirmationDocuments;
    }, [documentType, statementDocuments, confirmationDocuments]);

    useTrackViewOnFocus('Investment/Documents/Statements');

    return (
        <ScreenContainer title="Investment statements" hasBackButton>
            <YStack width="100%" gap={isNonMobileWeb ? 24 : 12}>
                {isLoadingDocuments ? (
                    <Spinner />
                ) : (
                    <>
                        <View width="100%">
                            <SegmentedControl
                                options={[
                                    {
                                        label: 'Statements',
                                        value: DocumentType.STATEMENT,
                                    },
                                    {
                                        label: 'Trade confirmations',
                                        value: DocumentType.TRADE_CONFIRMATION,
                                        flexBasis: isNonMobileWeb
                                            ? undefined
                                            : 40, // Prevent wrapping of label on small screens
                                    },
                                ]}
                                selected={documentType}
                                defaultValue={DocumentType.STATEMENT}
                                onValueChange={setDocumentType}
                            />
                        </View>

                        <DocumentLinkList
                            getDocumentDownloadUrl={getDocumentDownloadUrl}
                            accountDocuments={selectedDocuments ?? []}
                            documentTypeName={
                                DocumentTypeName[documentType] ??
                                'documents of this type'
                            }
                        />
                    </>
                )}
            </YStack>
        </ScreenContainer>
    );
};
